import React from 'react';
import styled from 'styled-components';
import { Button, ButtonIcon, Cell, Row, Text, TooltipIcon, TooltipWrapper, User } from 'components';
import { DEFAULT_ORG_ADMIN_JOB_TITLE } from 'config/const';
import { theme, toRem } from 'design';
import { EmployeeType, IUploadPayment } from 'types';
import { centsToDollarsDisplay, formatJobTitle } from 'utils';

interface IUploadPaymentSelectorProps {
  payment: IUploadPayment;
  disabled?: boolean;
  onSetEmployee(uploadPayment: IUploadPayment): void;
  onRemove(): void;
  tooltipId: string;
}

const StyledRow = styled(Row)`
  z-index: unset;

  @media (min-width: ${theme.breakpoint.small}px) {
    &:hover {
      cursor: pointer;
      .select-imported-item-button,
      .remove-imported-item-button {
        visibility: visible;
      }
    }

    .select-imported-item-button,
    .remove-imported-item-button {
      visibility: hidden;
    }
  }
`;

const EmployeeDetails = styled.div`
  display: flex;
  align-items: center;
  min-width: ${toRem(100)};
`;

const StyledText = styled(Text)`
  margin-left: ${toRem(theme.space.lg)};

  &.employee-mismatch-name {
    word-break: break-word;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
    width: 75%;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  flex-flow: row nowrap;
  flex-grow: 1;
  justify-content: space-evenly;
  gap: ${toRem(theme.space.xs)};
`;

const UploadPaymentSelector: React.FC<IUploadPaymentSelectorProps> = ({ payment, onRemove, onSetEmployee, disabled, tooltipId }) => (
  <StyledRow isActive={disabled} data-cy="upload-payment-row">
    <Cell>
      <EmployeeDetails>
        {!payment.employee ? (
          <>
            <TooltipWrapper>
              <TooltipIcon
                icon="Error"
                iconFill={{
                  default: payment.employeeMatch?.length > 1 ? theme.color.warning[50] : theme.color.danger[50],
                }}
                iconMarginRight={12}
                iconMarginLeft={0}
                iconSize={20}
                id={tooltipId}
              >
                {payment.employeeMatch?.length > 1 ? 'Multiple user matches found' : 'No user matches found'}
              </TooltipIcon>
            </TooltipWrapper>
            {payment.employeeId ? (
              <StyledText size="small" className="employee-mismatch-name">
                {payment.employeeId}
              </StyledText>
            ) : (
              <User stackable={false} id="employee-avatar" username={payment.name} wrapText />
            )}
          </>
        ) : (
          <>
            <TooltipWrapper>
              {disabled ? (
                <TooltipIcon icon="Error" iconMarginLeft={0} iconMarginRight={12} iconSize={20} id={tooltipId}>
                  You are not allowed to issue tips to this user.
                </TooltipIcon>
              ) : (
                <TooltipIcon
                  icon="Success"
                  iconFill={{
                    default: theme.color.success[50],
                  }}
                  iconMarginLeft={0}
                  iconMarginRight={12}
                  iconSize={20}
                  id={tooltipId}
                >
                  User matched
                </TooltipIcon>
              )}
            </TooltipWrapper>
            <User
              stackable={false}
              id={payment.employee.id}
              username={payment.employee.name}
              image={payment.employee.user?.avatarUrl}
              hasPaymentMethod={!!payment.employee.user?.card}
              wrapText
              role={formatJobTitle(payment.employee.type === EmployeeType.ADMIN ? DEFAULT_ORG_ADMIN_JOB_TITLE : payment.employee.sites[0]?.job.title)}
            />
          </>
        )}
      </EmployeeDetails>
    </Cell>
    {(payment.cash || payment.cash === 0) && <Cell data-cy="upload-payment-cash">{centsToDollarsDisplay(payment.cash)}</Cell>}
    <Cell data-cy="upload-payment-amount">{centsToDollarsDisplay(payment.amount)}</Cell>
    <Cell align="center">
      <ActionButtons>
        <Button className="select-imported-item-button" data-cy="select-imported-item-button" size="small" appearance="primary" label="Select Match" onClick={() => onSetEmployee(payment)} />
        <ButtonIcon className="remove-imported-item-button" data-cy="remove-imported-item-button" label="Delete import" icon="RemoveThick" appearance="destructive" size="small" onClick={onRemove} />
      </ActionButtons>
    </Cell>
  </StyledRow>
);

export default UploadPaymentSelector;
